import { siteSlugFromId } from '@atbash-labs/ontology-core'
import {
  intakeSiteId,
  type IngestIntake,
  type IngestIntakeItem,
  type IngestIntakeTabularTarget,
  type IngestIntakeUnstructuredTarget,
  type IngestMappingConfigSummary
} from '@axilon-apps/domains-ontology-ui-state'

import type { Status } from '../../../components/primitives/types'
import { formatRelativeTime } from '../../../lib/time'
import {
  extensionList,
  latestActiveVersion,
  refusedExtensions,
  versionsToUnarchive
} from '../../TabularConfigPage/util/configView'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const ACCEPT_PRESETS = ['xlsx', 'xlsm', 'xls', 'csv', 'pdf'] as const

/** Search param naming the expanded intake on the Ingest page's Intakes tab. */
export const INTAKE_PARAM = 'intake'

/**
 * The Intakes tab, optionally with one intake expanded. Links out of the tab
 * hand this to the destination as its "back" target so returning lands here
 * rather than on the default Runs tab.
 */
export function intakesTabPath(intakeId?: string): string {
  const params = new URLSearchParams({ tab: 'intakes' })
  if (intakeId) params.set(INTAKE_PARAM, intakeId)
  return `/ingest?${params.toString()}`
}

/** Merge review for an intake's branch, whose back link returns to the intake. */
export function intakeMergeReviewPath(branchId: string, intakeId: string): string {
  const params = new URLSearchParams({
    source: branchId,
    returnTo: intakesTabPath(intakeId),
    returnLabel: 'Intake endpoints'
  })
  return `/branches/merge?${params.toString()}`
}

export function siteUuid(value: string): string | null {
  const trimmed = value.trim()
  const candidate = siteSlugFromId(trimmed).trim()
  if (UUID_PATTERN.test(candidate)) return candidate.toLowerCase()
  if (trimmed.startsWith('urn:site:') || trimmed.includes('/site:')) {
    return intakeSiteId(trimmed)
  }
  return null
}

export function normalizeAccept(values: readonly string[]): string[] {
  const result: string[] = []
  const seen = new Set<string>()
  for (const raw of values) {
    const value = raw.trim().toLowerCase().replace(/^\.+/, '')
    if (!value || !/^[a-z0-9]{1,16}$/.test(value) || seen.has(value)) continue
    seen.add(value)
    result.push(value)
    if (result.length === 32) break
  }
  return result
}

export function acceptForRequest(values: readonly string[]): string[] {
  return normalizeAccept(values).map((value) => `.${value}`)
}

export function acceptForForm(values: readonly string[]): string[] {
  return normalizeAccept(values)
}

export function targetLabel(
  intake: IngestIntake,
  siteNames: Readonly<Record<string, string>> = {}
): string {
  if (!intake.targetKind || !intake.target) return 'no target'
  if (intake.targetKind === 'mapped_tabular_ingest') {
    const target = intake.target as IngestIntakeTabularTarget
    return `Tabular · ${target.configName} @${target.configVersion ? `v${target.configVersion}` : 'latest'}`
  }
  const target = intake.target as IngestIntakeUnstructuredTarget
  return `Unstructured · ${siteNames[target.siteId] ?? target.siteId}`
}

/**
 * Two-line target cell in the same shape as the jobs table's Type column:
 * the kind is the label, the specific config or site is the sub-line.
 */
export interface IntakeTargetCell {
  kind: 'none' | 'tabular' | 'unstructured'
  label: string
  detail?: string
  configName?: string
  /** Set when the version the target runs is archived, so every new file fails. */
  archivedNote?: string
  /** The intake's file types the version the target runs refuses, so their files fail. */
  refusedExtensions?: string[]
  /** What refuses them and how to fix it. */
  refusedNote?: string
}

/**
 * `configs` is every tabular config row, archived ones included; without it
 * no target is flagged.
 */
export function targetCell(
  intake: IngestIntake,
  siteNames: Readonly<Record<string, string>> = {},
  configs: readonly IngestMappingConfigSummary[] = []
): IntakeTargetCell {
  if (!intake.targetKind || !intake.target) return { kind: 'none', label: 'No target' }
  if (intake.targetKind === 'mapped_tabular_ingest') {
    const target = intake.target as IngestIntakeTabularTarget
    const archivedNote = archivedTargetNote(target, configs)
    const refused = archivedNote ? undefined : refusedTypes(intake.accept, target, configs)
    return {
      kind: 'tabular',
      label: 'Tabular',
      detail: `${target.configName} @${target.configVersion ? `v${target.configVersion}` : 'latest'}`,
      configName: target.configName,
      ...(archivedNote ? { archivedNote } : {}),
      ...(refused ? { refusedExtensions: refused.extensions, refusedNote: refused.note } : {})
    }
  }
  const target = intake.target as IngestIntakeUnstructuredTarget
  return {
    kind: 'unstructured',
    label: 'Unstructured',
    detail: siteNames[target.siteId] ?? target.siteId
  }
}

function archivedTargetNote(
  target: IngestIntakeTabularTarget,
  configs: readonly IngestMappingConfigSummary[]
): string | undefined {
  const named = configs.filter((row) => row.name === target.configName)
  const scope = target.configVersion ? { version: target.configVersion } : {}
  if (versionsToUnarchive(named, scope).length === 0) return undefined
  if (target.configVersion) {
    return `${target.configName} v${target.configVersion} is archived. New files fail until this intake points at an active version or is turned off.`
  }
  return `Every version of ${target.configName} is archived. New files fail until this intake points at another config or is turned off.`
}

function refusedTypes(
  accept: readonly string[],
  target: IngestIntakeTabularTarget,
  configs: readonly IngestMappingConfigSummary[]
): { extensions: string[]; note: string } | undefined {
  const named = configs.filter((row) => row.name === target.configName)
  const version = target.configVersion
    ? named.find((row) => row.version === target.configVersion)
    : latestActiveVersion(named)
  const configAccept = version?.definition.input?.accept
  const extensions = refusedExtensions(configAccept, accept)
  if (!version || !configAccept || extensions.length === 0) return undefined
  return {
    extensions,
    note: `${target.configName} v${version.version} accepts only ${extensionList(configAccept)}, so ${extensionList(extensions)} files fail. Remove those types from this intake, or publish a version of ${target.configName} that accepts them.`
  }
}

/**
 * Config version ids whose archive is blocking this intake. A pin restores
 * only that version; `@latest` restores every archived version when none is
 * still active, and nothing when a version is already current.
 */
export function intakeUnarchiveVersionIds(
  intake: IngestIntake,
  configs: readonly IngestMappingConfigSummary[]
): string[] {
  if (intake.targetKind !== 'mapped_tabular_ingest' || !intake.target) return []
  const target = intake.target as IngestIntakeTabularTarget
  const named = configs.filter((row) => row.name === target.configName)
  const scope = target.configVersion ? { version: target.configVersion } : {}
  return versionsToUnarchive(named, scope).map((row) => row.id)
}

export function branchCell(intake: IngestIntake): string {
  return intake.branch ? intake.branch.name : `${intake.branchName} · nothing unmerged`
}

export interface IntakeStatusView {
  label: string
  status: Status
  tooltip?: string
}

/**
 * Pills for received files an operator still has to act on, in the wording of
 * the ledger's own status pills. Empty when nothing needs attention.
 */
export function attentionPills(intake: IngestIntake): IntakeStatusView[] {
  const failed = intake.attention?.failed ?? 0
  const awaitingRebuild = intake.attention?.awaitingRebuild ?? 0
  const awaitingConfirmation = intake.attention?.awaitingConfirmation ?? 0
  const pills: IntakeStatusView[] = []
  if (awaitingConfirmation > 0) {
    pills.push({
      label: `${awaitingConfirmation} to confirm`,
      status: 'warn',
      tooltip: `${awaitingConfirmation === 1 ? '1 file has' : `${awaitingConfirmation} files have`} the name of an earlier file and would replace the rows it wrote. Open the intake to replace or discard ${awaitingConfirmation === 1 ? 'it' : 'them'}.`
    })
  }
  if (failed > 0) {
    pills.push({
      label: `${failed} failed`,
      status: 'down',
      tooltip: `${failed === 1 ? '1 file' : `${failed} files`} failed since the last file that succeeded. Open the intake to see ${failed === 1 ? 'it' : 'them'}.`
    })
  }
  if (awaitingRebuild > 0) {
    pills.push({
      label: 'config update needed',
      status: 'warn',
      tooltip: `${awaitingRebuild === 1 ? '1 file is' : `${awaitingRebuild} files are`} waiting for the mapped tabular config to be updated.`
    })
  }
  return pills
}

/**
 * `intake` tells a pending file that will start on its own apart from one
 * that waits for a target or for the intake to be switched on.
 */
export function statusPill(
  item: IngestIntakeItem,
  intake?: Pick<IngestIntake, 'enabled' | 'targetKind' | 'target'>
): IntakeStatusView {
  switch (item.status) {
    case 'pending':
      return pendingPill(intake)
    case 'awaiting_confirmation':
      return {
        label: 'confirm replace',
        status: 'warn',
        tooltip:
          'An earlier file of this config has the same name. This file waits until someone replaces the rows that file wrote with this one, or discards it. Later files keep running.'
      }
    case 'running':
      return { label: 'running', status: 'info' }
    case 'awaiting_rebuild':
      return {
        label: 'config update needed',
        status: 'warn',
        tooltip: 'The mapped tabular config must be updated before this file can continue.'
      }
    case 'awaiting_review':
      return {
        label: 'needs review',
        status: 'warn',
        tooltip:
          'Held for review: nothing is committed until each flagged page is reviewed or excluded on the run page. Later files keep running; when their order matters to the config, they commit only after this one.'
      }
    case 'awaiting_turn':
      return {
        label: 'waiting',
        status: 'info',
        tooltip:
          'The rows are ready. This intake commits files in the order they arrived, so this file commits once the earlier ones are committed or discarded.'
      }
    case 'done':
      return { label: 'done', status: 'run', tooltip: resultSummary(item) }
    case 'skipped':
      return {
        label: 'skipped',
        status: 'idle',
        tooltip: item.skippedBy
          ? `Skipped by ${item.skippedBy} ${formatRelativeTime(item.skippedAt)}`.trim()
          : undefined
      }
    case 'failed':
      if (item.acknowledged) {
        return { label: 'failed · ignored', status: 'idle', tooltip: item.error ?? undefined }
      }
      return { label: 'failed', status: 'down', tooltip: item.error ?? undefined }
  }
}

function pendingPill(
  intake?: Pick<IngestIntake, 'enabled' | 'targetKind' | 'target'>
): IntakeStatusView {
  if (intake && (!intake.targetKind || !intake.target)) {
    return {
      label: 'waiting for target',
      status: 'idle',
      tooltip:
        'This intake has no target, so received files wait. Set a target to process them oldest first.'
    }
  }
  if (intake && intake.targetKind !== 'mapped_tabular_ingest') {
    return {
      label: 'waiting for target',
      status: 'idle',
      tooltip:
        'Only a tabular target processes received files automatically, so they wait. Set one to process them oldest first.'
    }
  }
  if (intake && !intake.enabled) {
    return {
      label: 'intake off',
      status: 'idle',
      tooltip:
        'This intake is switched off. Turn it on to process files oldest first, or use Run now on the oldest file.'
    }
  }
  return {
    label: 'starting',
    status: 'info',
    tooltip: 'Automatic processing is starting for this file.'
  }
}

function resultSummary(item: IngestIntakeItem): string | undefined {
  if (!item.result) return undefined
  const rows = Object.entries(item.result).map(
    ([table, counts]) =>
      `${table}: ${counts.inserted} inserted, ${counts.updated} updated, ${counts.skipped} skipped`
  )
  return rows.length > 0 ? rows.join('\n') : undefined
}
